import * as React from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Check } from 'lucide-react';
import { z } from 'zod';
import {
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceInput,
  WorkspaceNotice,
  useToast,
} from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { ApiError } from '@/lib/api/client';
import { useEngagementQuery } from '@/lib/api/engagements';
import { newIdempotencyKey } from '@/lib/api/engagement-requirements';
import {
  DuplicateRowSchema,
  useDiscoverySetupQuery,
  useDiscoverySummaryQuery,
  useStartDiscoveryMutation,
  type DuplicateRow,
  type SetupFramework,
  type StartResult,
} from '@/lib/api/discovery';
import { cn } from '@/lib/utils/cn';
import { ClientFactsDialog, ClientFactsSummary } from '../client-facts/client-facts';
import {
  buildStartBody,
  emailProblem,
  emptyDraft,
  nameProblem,
  planGroups,
  plural,
  scheduledRows,
  type DuplicateAnswer,
  type PlanDraft,
} from '../discovery-logic';

/**
 * Discovery -> Set up (T-0185, E12-F02): the first-run screen. Three
 * things, all changeable later: the framework, the client's facts, and
 * who will be interviewed from the framework's starting plan. Prototype:
 * `setupView` in `docs/discovery/prototype/discovery.js`.
 *
 *   - Any member with edit rights can start (Decision 54.8).
 *   - Dates schedule those interviews; starting asks before any invite is
 *     sent (Decision 53.16), and remembers the answer until a date changes.
 *   - A name that may be someone already named is refused by the API with
 *     the candidates; each is answered here, then Start runs again (rule 6).
 *   - Interview codes are S1…Sn in plan order, assigned by the server on
 *     start (rule 20); the screen shows the code each row will get.
 */

type Questions = Record<number, DuplicateRow['candidates']>;

export function DiscoverySetup({ engagementId }: { engagementId: string }) {
  const setupQ = useDiscoverySetupQuery(engagementId);
  const summaryQ = useDiscoverySummaryQuery(engagementId);
  const engagement = useEngagementQuery(engagementId);
  const start = useStartDiscoveryMutation(engagementId);
  const navigate = useNavigate();
  const { toast } = useToast();

  const frameworks = setupQ.data ?? [];
  const [frameworkId, setFrameworkId] = React.useState<string | null>(null);
  const chosen =
    frameworks.find((f) => f.framework.id === frameworkId) ??
    (frameworks.length === 1 ? frameworks[0] : undefined);
  const plan = React.useMemo(() => chosen?.plan ?? [], [chosen]);

  const [drafts, setDrafts] = React.useState<PlanDraft[]>([]);
  const [questions, setQuestions] = React.useState<Questions>({});
  const [answers, setAnswers] = React.useState<Record<number, DuplicateAnswer>>({});
  const [rowErrors, setRowErrors] = React.useState<Record<number, string>>({});
  const [inviteChoice, setInviteChoice] = React.useState<boolean | null>(null);
  const [askInvites, setAskInvites] = React.useState(false);
  const [factsOpen, setFactsOpen] = React.useState(false);
  const [error, setError] = React.useState<unknown>(null);

  // A different framework is a different plan: start its rows empty.
  React.useEffect(() => {
    setDrafts(plan.map(emptyDraft));
    setQuestions({});
    setAnswers({});
    setRowErrors({});
    setInviteChoice(null);
  }, [plan]);

  const forget = (i: number) => {
    const drop = <T,>(m: Record<number, T>) => {
      const next = { ...m };
      delete next[i];
      return next;
    };
    setQuestions(drop);
    setAnswers(drop);
    setRowErrors(drop);
  };
  const edit = (i: number, patch: Partial<PlanDraft>) => {
    setDrafts((prev) => prev.map((d, j) => (j === i ? { ...d, ...patch } : d)));
    if (patch.name !== undefined || patch.email !== undefined) forget(i);
    if (patch.when !== undefined) {
      setInviteChoice(null);
      setRowErrors((m) => {
        const next = { ...m };
        delete next[i];
        return next;
      });
    }
  };

  const canStart = summaryQ.data?.permissions.can_start ?? false;
  const scheduled = scheduledRows(drafts);
  const unanswered = Object.keys(questions).filter((k) => !answers[Number(k)]).length;
  const problems = drafts.some((d) => nameProblem(d.name) || emailProblem(d.email));
  const blocked = !chosen || !canStart || start.isPending || unanswered > 0 || problems;

  const submit = (sendInvites: boolean | null) => {
    if (!chosen) return;
    setError(null);
    const body = buildStartBody({
      frameworkId: chosen.framework.id,
      plan,
      drafts,
      questions,
      answers,
      sendInvites,
    });
    start.mutate(
      { body, key: newIdempotencyKey() },
      {
        onSuccess: (r) => {
          toast({ message: startedMessage(chosen, r, scheduled.length > 0) });
          void navigate({ to: '/engagements/$engagementId/discovery', params: { engagementId } });
        },
        onError: (e) => {
          const reason = e instanceof ApiError ? e.details?.['reason'] : undefined;
          const rows =
            reason === 'person.possible_duplicate'
              ? z
                  .array(DuplicateRowSchema)
                  .safeParse(e instanceof ApiError ? e.details?.['rows'] : [])
              : null;
          if (rows?.success) {
            setQuestions(Object.fromEntries(rows.data.map((r) => [r.index, r.candidates])));
            return;
          }
          const index = e instanceof ApiError ? e.details?.['index'] : undefined;
          if (typeof index === 'number') setRowErrors({ [index]: (e as Error).message });
          else setError(e);
        },
      },
    );
  };

  const onStart = () => {
    if (blocked) return;
    if (scheduled.length > 0 && inviteChoice === null) setAskInvites(true);
    else submit(scheduled.length > 0 ? inviteChoice : null);
  };
  const answerInvites = (send: boolean) => {
    setInviteChoice(send);
    setAskInvites(false);
    submit(send);
  };

  const client = engagement.data?.client_name;
  const noEmail = scheduled.filter((i) => !drafts[i]?.email.trim()).length;

  return (
    <div className="min-h-0 flex-1 overflow-auto bg-paper-50">
      <div className="mx-auto w-full max-w-[1040px] px-6 pb-12 pt-8">
        <div className="mb-6">
          <h1 className="text-display font-semibold text-ink-900">
            Start discovery{client ? ` for ${client}` : ''}
          </h1>
          <p className="text-body text-ink-500">
            Three things, all changeable later. Nothing is sent to anyone yet.
          </p>
        </div>

        <Step n={1} title="Choose the framework">
          <p className="text-meta font-medium text-ink-500">
            The engagement takes a copy of this version. Later edits in Firm settings don’t change
            it.
          </p>
          {setupQ.error ? <ErrorMessage error={setupQ.error} className="mt-3" /> : null}
          {!setupQ.data && !setupQ.error ? <Spinner className="mt-3" /> : null}
          {setupQ.data && frameworks.length === 0 ? (
            <WorkspaceNotice className="mt-3">
              No discovery framework is published yet. A firm admin publishes one in Firm settings →
              Discovery frameworks.
            </WorkspaceNotice>
          ) : null}
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            {frameworks.map((f) => {
              const on = chosen?.framework.id === f.framework.id;
              return (
                <button
                  key={f.framework.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setFrameworkId(f.framework.id)}
                  className={cn(
                    'flex flex-col gap-2 rounded-large border p-5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600',
                    on
                      ? 'border-accent-amber-600 bg-accent-amber-50'
                      : 'border-ink-100 bg-paper-0 hover:border-ink-300',
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span className="text-[15px] font-semibold text-ink-900">
                      {f.framework.name}
                    </span>
                    <span className="inline-flex h-[22px] items-center rounded-full border border-ink-100 bg-paper-100 px-2.5 text-meta font-medium text-ink-700">
                      v{f.version.version}
                    </span>
                    {on ? (
                      <Check className="ml-auto h-4 w-4 text-ink-900" aria-hidden="true" />
                    ) : null}
                  </span>
                  <span className="text-body-sm text-ink-500">
                    {plural(f.counts.areas, 'area')} ·{' '}
                    {plural(f.counts.interviews, 'planned interview')} ·{' '}
                    {plural(f.counts.questionnaires, 'questionnaire')}
                  </span>
                  <span className="text-body-sm text-ink-500">
                    {f.version.sources_required === 1
                      ? 'Corroboration is turned off: one source covers an area.'
                      : `Each area needs ${f.version.sources_required} independent sources.`}
                  </span>
                </button>
              );
            })}
          </div>
        </Step>

        <Step n={2} title="Client facts">
          <p className="mb-3 text-meta font-medium text-ink-500">
            These decide which statutory packs and vendors apply later. Each cites where it came
            from. They belong to the client, so every engagement of the client shares them. Nothing
            here is filled in by AI.
          </p>
          <ClientFactsSummary engagementId={engagementId} />
          <WorkspaceButton className="mt-3" onClick={() => setFactsOpen(true)}>
            {summaryQ.data?.permissions.can_edit_client_facts
              ? 'Edit client facts'
              : 'View client facts'}
          </WorkspaceButton>
        </Step>

        <Step n={3} title="Who you’ll interview">
          <p className="text-meta font-medium text-ink-500">
            The framework’s starting plan, one interview per role. Name the people now or later;
            each becomes a person record, so their evidence is counted once. A date schedules the
            interview.
          </p>
          {!chosen ? (
            <p className="mt-3 text-body-sm text-ink-500">
              Choose a framework to see its interview plan.
            </p>
          ) : (
            <div className="mt-3">
              {planGroups(plan).map((g) => (
                <div key={g.name}>
                  <div className="pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">
                    {g.name}
                  </div>
                  {g.rows.map(({ index: i, role, code }) => (
                    <PlanRow
                      key={i}
                      code={code}
                      role={role.role}
                      optional={role.optional}
                      draft={drafts[i] ?? emptyDraft()}
                      error={rowErrors[i] ?? null}
                      candidates={questions[i]}
                      answer={answers[i]}
                      disabled={!canStart || start.isPending}
                      onEdit={(patch) => edit(i, patch)}
                      onAnswer={(a) => setAnswers((m) => ({ ...m, [i]: a }))}
                      onReopen={() =>
                        setAnswers((m) => {
                          const next = { ...m };
                          delete next[i];
                          return next;
                        })
                      }
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </Step>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-ink-100 pt-4">
          {unanswered > 0 ? (
            <span className="mr-auto text-meta font-medium text-ink-500">
              Answer {unanswered === 1 ? 'the name question' : `the ${unanswered} name questions`}{' '}
              above, then start.
            </span>
          ) : !canStart && summaryQ.data ? (
            <span className="mr-auto text-meta font-medium text-ink-500">
              You can view this discovery but not start it.
            </span>
          ) : null}
          {error ? <ErrorMessage error={error} className="w-full" /> : null}
          <WorkspaceButton size="md" variant="tertiary" asChild>
            <Link to="/engagements/$engagementId" params={{ engagementId }}>
              Cancel
            </Link>
          </WorkspaceButton>
          <WorkspaceButton size="md" variant="primary" disabled={blocked} onClick={onStart}>
            {start.isPending ? 'Starting…' : 'Start discovery'}
          </WorkspaceButton>
        </div>
      </div>

      <WorkspaceDialog
        open={askInvites}
        onOpenChange={setAskInvites}
        title={`Send invites for ${plural(scheduled.length, 'scheduled interview')}?`}
        description="You gave dates for these. Each person gets a calendar invite with the interview’s purpose."
        actions={
          <>
            <WorkspaceButton size="md" variant="tertiary" onClick={() => answerInvites(false)}>
              Not yet
            </WorkspaceButton>
            <WorkspaceButton size="md" variant="primary" onClick={() => answerInvites(true)}>
              Send invites
            </WorkspaceButton>
          </>
        }
      >
        <p className="text-body-sm text-ink-700">
          You can also send them later from each interview.
          {noEmail > 0
            ? ` ${plural(noEmail, 'scheduled interview')} ${noEmail === 1 ? 'has' : 'have'} no one with an email yet, so no invite can go there.`
            : ''}
        </p>
      </WorkspaceDialog>
      <ClientFactsDialog engagementId={engagementId} open={factsOpen} onOpenChange={setFactsOpen} />
    </div>
  );
}

function startedMessage(f: SetupFramework, r: StartResult, scheduled: boolean): string {
  const head = `Discovery started on ${f.framework.name} v${r.framework_version}.`;
  const invites =
    r.invites_sent > 0
      ? ` ${plural(r.invites_sent, 'invite')} sent.`
      : scheduled
        ? ' No invites sent; send them from each interview when ready.'
        : ' The engagement keeps this copy of the framework.';
  const failed = r.invites_failed.length
    ? ` ${plural(r.invites_failed.length, 'invite')} could not be sent; send ${r.invites_failed.length === 1 ? 'it' : 'them'} again from the interview.`
    : '';
  return head + invites + failed;
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section aria-label={title} className="flex gap-4 border-t border-ink-100 py-6">
      <span
        aria-hidden="true"
        className="inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-ink-900 text-body-sm font-semibold text-paper-0"
      >
        {n}
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-h2 font-semibold text-ink-900">{title}</h2>
        {children}
      </div>
    </section>
  );
}

function PlanRow({
  code,
  role,
  optional,
  draft,
  error,
  candidates,
  answer,
  disabled,
  onEdit,
  onAnswer,
  onReopen,
}: {
  code: string;
  role: string;
  optional: boolean;
  draft: PlanDraft;
  error: string | null;
  candidates: DuplicateRow['candidates'] | undefined;
  answer: DuplicateAnswer | undefined;
  disabled: boolean;
  onEdit: (patch: Partial<PlanDraft>) => void;
  onAnswer: (a: DuplicateAnswer) => void;
  onReopen: () => void;
}) {
  const problem = error ?? nameProblem(draft.name) ?? emailProblem(draft.email);
  const who = `${code} ${role}`;
  const candidateName = (c: DuplicateRow['candidates'][number]) =>
    `${c.full_name}${c.plan_index != null ? ` (S${c.plan_index + 1})` : c.title ? `, ${c.title}` : ''}`;
  return (
    <div className="py-1.5" data-row={code}>
      <div className="grid grid-cols-[44px_minmax(150px,1fr)_minmax(170px,1.1fr)_minmax(170px,1.1fr)_200px] items-center gap-3">
        <span className="inline-flex h-6 min-w-9 items-center justify-center rounded-chip border border-ink-100 bg-paper-100 px-1.5 font-mono text-meta font-semibold text-ink-900">
          {code}
        </span>
        <span className="min-w-0 text-body-sm font-medium text-ink-900">
          {role}
          {optional ? (
            <span className="ml-1 text-meta font-medium text-ink-500">optional</span>
          ) : null}
        </span>
        <WorkspaceInput
          aria-label={`Name for ${who}`}
          placeholder="Name (or later)"
          value={draft.name}
          disabled={disabled}
          onChange={(e) => onEdit({ name: e.target.value })}
        />
        <WorkspaceInput
          aria-label={`Email for ${who}`}
          type="email"
          placeholder="Email, for the invite"
          value={draft.email}
          disabled={disabled}
          onChange={(e) => onEdit({ email: e.target.value })}
        />
        <WorkspaceInput
          aria-label={`Date and time for ${who}`}
          type="datetime-local"
          value={draft.when}
          disabled={disabled}
          onChange={(e) => onEdit({ when: e.target.value })}
        />
      </div>
      {problem ? (
        <p role="alert" className="ml-[56px] mt-1 text-meta font-medium text-posture-critical">
          {problem}
        </p>
      ) : null}
      {candidates && !answer ? (
        <div
          role="group"
          aria-label={`Is ${draft.name} someone already named?`}
          className="ml-[56px] mt-2 flex flex-wrap items-center gap-2.5 rounded-card border border-accent-amber-100 bg-accent-amber-50 px-3.5 py-3"
        >
          <AlertTriangle className="h-4 w-4 flex-none text-accent-amber-700" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <div className="text-body-sm font-medium text-ink-900">
              Is “{draft.name}” the same person as {candidates.map(candidateName).join(' or ')}?
            </div>
            <div className="text-meta font-medium text-ink-500">
              Using the same person keeps everything they say as one source.
            </div>
          </div>
          {candidates.map((c, k) => (
            <WorkspaceButton
              key={k}
              variant="primary"
              onClick={() =>
                onAnswer(
                  c.person_id
                    ? { decision: 'same', person_id: c.person_id }
                    : { decision: 'same', plan_index: c.plan_index! },
                )
              }
            >
              {candidates.length === 1 ? 'Yes, same person' : `Same as ${c.full_name}`}
            </WorkspaceButton>
          ))}
          <WorkspaceButton variant="tertiary" onClick={() => onAnswer({ decision: 'different' })}>
            {candidates.length === 1 ? 'No, a different person' : 'None of them'}
          </WorkspaceButton>
        </div>
      ) : null}
      {candidates && answer ? (
        <div className="ml-[56px] mt-2 flex items-center gap-2.5 rounded-card border border-posture-healthy bg-posture-healthy-tint px-3.5 py-2 text-body-sm text-ink-900">
          <Check className="h-4 w-4 flex-none text-posture-healthy" aria-hidden="true" />
          <span className="flex-1">
            {answer.decision === 'different'
              ? `A different person from ${candidates.map((c) => c.full_name).join(' and ')}.`
              : `The same person as ${candidates.find((c) => ('plan_index' in answer ? c.plan_index === answer.plan_index : c.person_id === answer.person_id))?.full_name ?? 'the one named'}.`}
          </span>
          <button
            type="button"
            className="text-meta font-medium text-ink-700 underline underline-offset-[3px]"
            onClick={onReopen}
          >
            Change
          </button>
        </div>
      ) : null}
    </div>
  );
}
